/* 0.41.16 — Reading values out of an API response.
 *
 * A rule refers to a response value by PATH:
 *
 *     total_matches                 a scalar
 *     results[0].mark_id            one row
 *     results[].image_url           every row's value, as an array
 *     results[].signals.phash       nested, across rows
 *
 * WHY `[]` IS NOT THE SAME AS `[0]`
 *
 * `[]` is a PROJECTION: it walks every element and collects the rest
 * of the path from each. That is what makes parallel arrays possible -
 * `results[].image_url` and `results[].mark_id` are two arrays a table
 * can render side by side.
 *
 * MISSING VALUES BECOME null, NOT SKIPPED
 *
 * This is the decision that matters, and the real trademark response
 * shows why: its two rows carry DIFFERENT signal keys - the first has
 * `concept_embedding`, the second does not.
 *
 * If a projection skipped missing values, `results[].signals.phash`
 * and `results[].signals.concept_embedding` would come back with
 * different lengths, and row 2 of one array would line up with row 3
 * of the other. Every parallel array would silently misalign whenever
 * the far end omitted a field.
 *
 * So a missing value yields null and the array keeps its length. An
 * array of results always has as many entries as there are rows.
 */

import { formatCalendarDate, formatCalendarDateTime } from '../utils/format';

// 0.43.46 - A key may end in »: `text»` is the JSON or XML read from
// inside the text value `text` (see expandEmbedded below).
const SEGMENT = /^([A-Za-z_][A-Za-z0-9_]*»?)?((?:\[\d*\])*)$/;

/** Split "results[].signals.phash" into walkable steps. */
export function parsePath(path) {
  const steps = [];
  for (const raw of String(path || '').split('.')) {
    const part = raw.trim();
    if (!part) continue;
    const m = SEGMENT.exec(part);
    if (!m) return null;              // malformed - caller reports it
    if (m[1]) steps.push({ kind: 'key', name: m[1] });
    for (const br of (m[2] || '').match(/\[\d*\]/g) || []) {
      const inner = br.slice(1, -1);
      steps.push(inner === ''
        ? { kind: 'all' }
        : { kind: 'index', index: parseInt(inner, 10) });
    }
  }
  return steps;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;
function localOne(shown, iso) {
  if (typeof iso !== 'string' || !iso) return shown;
  if (ISO_DATE.test(iso)) return formatCalendarDate(iso);
  if (ISO_DATETIME.test(iso)) return formatCalendarDateTime(iso);
  return shown;
}
/* undefined = not a converted date, read the value as it is. */
function localDate(shown, iso) {
  if (iso === undefined || iso === null) return undefined;
  if (Array.isArray(shown) && Array.isArray(iso)) {
    return shown.map((v, i) => localOne(v, iso[i]));
  }
  if (typeof iso === 'string' && iso && (typeof shown === 'string' || typeof shown === 'number')) {
    return localOne(shown, iso);
  }
  return undefined;
}

function walk(node, steps) {
  if (steps.length === 0) return node === undefined ? null : node;
  const [step, ...rest] = steps;

  if (step.kind === 'key') {
    if (node === null || typeof node !== 'object' || Array.isArray(node)) {
      return null;
    }
    // 0.46.27 - A date the connection converted (Expected results,
    // marked as a date) arrives as display text written on the server,
    // with English month names, and its ISO value beside it as
    // `<key>_iso`. Read for showing, it is written again here from the
    // ISO value, in the page's language (Months and days library) and
    // the Public site format, as every other date on the page is.
    if (rest.length === 0) {
      const local = localDate(node[step.name], node[`${step.name}_iso`]);
      if (local !== undefined) return local;
    }
    return walk(node[step.name], rest);
  }

  if (step.kind === 'index') {
    // 0.41.56 - A single item counts as a one-item list, for the same
    // reason as the projection below.
    if (!Array.isArray(node)) {
      return step.index === 0 && node !== null && node !== undefined
        ? walk(node, rest)
        : null;
    }
    return walk(node[step.index], rest);
  }

  /* 'all' — the projection. Length is preserved deliberately; see the
   * note at the top of this file.
   *
   * 0.41.56 - A NON-list is treated as a list of one.
   *
   * XML has no way to say "this is a list": repetition is the only
   * signal, so two <Rate> elements give an array and one gives an
   * object. A table configured on `Rate[].ISO` would then show
   * everything on a busy day and nothing on a quiet one - the worst
   * kind of failure, because it looks like no data rather than a
   * mistake.
   *
   * The `[]` in the path is the author saying they expect many. When
   * the data carries one, that is still one of many. Null and
   * undefined stay empty, because "absent" is not "one". */
  if (!Array.isArray(node)) {
    if (node === null || node === undefined) return null;
    return [walk(node, rest)];
  }
  return node.map(item => walk(item, rest));
}

/**
 * Read `path` out of `response`.
 * Returns null for anything missing, so a caller never has to
 * distinguish "absent" from "absent at a different depth".
 */
export function readPath(response, path) {
  const steps = parsePath(path);
  if (steps === null) return null;
  return walk(response, steps);
}

/** Whether a path is syntactically valid — for save-time checking. */
export function isValidPath(path) {
  const steps = parsePath(path);
  return steps !== null && steps.length > 0;
}


/* 0.41.32 — Derive the available paths from a sample response.
 *
 * An admin should not have to read JSON and work out the syntax to
 * write `results[].mark_id`. The Test panel has already shown them a
 * real response; this turns that response into a list of paths they
 * can pick.
 *
 * ARRAYS COLLAPSE TO `[]`, NOT `[0]`, `[1]`, `[2]`.
 *
 * A search returning 31 results would otherwise produce 31 near
 * identical paths per field. What an admin almost always wants is
 * "this field from every row", which is exactly what `[]` means. The
 * FIRST element is walked for structure, since rows of a list share a
 * shape; a row missing a key contributes nothing, which is why the
 * list is a suggestion rather than a guarantee.
 *
 * Values are sampled for display only — never stored. A response can
 * carry personal data, and only the SHAPE is worth keeping.
 */
const MAX_PATHS = 200;

export function derivePaths(response, { withSamples = true } = {}) {
  const out = [];
  const seen = new Set();

  const sample = (v) => {
    if (!withSamples) return undefined;
    if (v === null) return 'null';
    if (Array.isArray(v)) return `${v.length} item${v.length === 1 ? '' : 's'}`;
    if (typeof v === 'object') return '{…}';
    const s = String(v);
    return s.length > 40 ? s.slice(0, 40) + '…' : s;
  };

  const walk = (node, path, depth) => {
    // 0.41.55 - Depth 12, matching the backend. A SOAP response spends
    // four levels on envelope boilerplate before any data, so six cut
    // off the rows and left only the shallow fields.
    if (out.length >= MAX_PATHS || depth > 12) return;

    if (Array.isArray(node)) {
      if (node.length === 0) return;
      // 0.41.36 - Walk EVERY row, not just the first.
      //
      // Rows of one list do not have to share a shape: the trademark
      // response's matches carry different `signals` keys. Sampling
      // node[0] offered whatever the first row happened to have, so a
      // field present only in later rows was invisible - and an admin
      // had no way to learn it existed.
      //
      // Duplicates are dropped below, so the cost is a walk rather
      // than a longer list. Capped, so a 500-row response does not
      // turn discovery into a scan.
      const rows = node.slice(0, 50);
      for (const row of rows) walk(row, `${path}[]`, depth + 1);
      return;
    }

    if (node !== null && typeof node === 'object') {
      for (const key of Object.keys(node)) {
        const next = path ? `${path}.${key}` : key;
        const value = node[key];
        if (value !== null && typeof value === 'object') {
          // A leaf worth offering in its own right (an array of
          // scalars reads fine as a joined value), then its innards.
          // 0.49.28 - An EMPTY list is offered too (payments: [] gives
          // payments[]), and an empty object as itself: they have no
          // fields to walk and used to vanish, as if the key were not
          // in the answer. Their fields come with an answer that has
          // items. Matches derive_paths on the server.
          if (!Array.isArray(value) && Object.keys(value).length === 0 && !seen.has(next)) {
            seen.add(next);
            out.push({ path: next, sample: sample(value) });
          }
          if (Array.isArray(value)
              && (value.length === 0 || typeof value[0] !== 'object')) {
            if (!seen.has(`${next}[]`)) {
              seen.add(`${next}[]`);
              out.push({ path: `${next}[]`, sample: sample(value) });
            }
          }
          walk(value, next, depth + 1);
        } else if (!seen.has(next)) {
          // First row to offer this path wins its example value: a
          // later row's value is no more representative, and showing
          // the same path twice would be noise.
          seen.add(next);
          out.push({ path: next, sample: sample(value) });
        }
        if (out.length >= MAX_PATHS) return;
      }
      return;
    }
  };

  walk(response, '', 0);
  return out;
}


/* 0.41.36 — Is this response a tree?
 *
 * A key is RECURSIVE when its array items carry that same key again:
 * `units` whose members each have their own `units`. That is what an
 * org structure looks like, and what a flat result list (the trademark
 * search) does not.
 *
 * Detected rather than declared. The platform has a real response in
 * front of it after a test; asking an admin to tell it what it can
 * read for itself is the sort of question a wizard should not need to
 * ask.
 *
 * Informational for now. Nothing acts on it: the table source that
 * would flatten a tree does not exist yet, and a setting with no
 * consumer is a setting nobody can be sure is correct.
 */
export function detectHierarchy(response, { maxDepth = 6 } = {}) {
  const found = new Map();

  const walk = (node, path, depth) => {
    if (depth > maxDepth || node === null || typeof node !== 'object') return;
    if (Array.isArray(node)) {
      if (node.length) walk(node[0], path, depth + 1);
      return;
    }
    for (const key of Object.keys(node)) {
      const value = node[key];
      const here = path ? `${path}.${key}` : key;
      if (Array.isArray(value) && value.length
          && value[0] !== null && typeof value[0] === 'object'
          && key in value[0]) {
        // Record the SHALLOWEST path for each repeating key: the same
        // key recurs at every level, and listing units, units.units,
        // units.units.units says one thing three times.
        if (!found.has(key)) found.set(key, here);
      }
      walk(value, here, depth + 1);
    }
  };

  walk(response, '', 0);
  return Array.from(found.entries()).map(([key, path]) => ({ key, path }));
}

/* Deepest nesting of a recursive key in this particular response.
 * A sample says how deep THIS answer went, never how deep the data can
 * go - so it is reported as "at least", not as a limit. */
export function hierarchyDepth(response, key, depth = 0) {
  if (response === null || typeof response !== 'object') return depth;
  if (Array.isArray(response)) {
    return response.reduce((m, r) => Math.max(m, hierarchyDepth(r, key, depth)), depth);
  }
  const child = response[key];
  if (Array.isArray(child) && child.length) {
    return child.reduce((m, r) => Math.max(m, hierarchyDepth(r, key, depth + 1)), depth + 1);
  }
  let best = depth;
  for (const v of Object.values(response)) {
    if (v !== null && typeof v === 'object') {
      best = Math.max(best, hierarchyDepth(v, key, depth));
    }
  }
  return best;
}


/* 0.41.38 — Group derived paths by what they repeat over.
 *
 * `[]` in a path is the whole rule: no `[]` means one value for the
 * response, `[]` means one value per item of that list. The UI already
 * enforces it — "Rows from" offers only list paths, columns only
 * row-relative ones — but nothing ever SAID it, so an admin had to
 * deduce the convention from which dropdown accepted what.
 *
 * Grouping states it. Each group also carries how many items its list
 * held in this sample, because "one per match (2)" explains a
 * two-row table far better than the path syntax does.
 */
export function groupPaths(paths, response) {
  const groups = new Map();

  for (const entry of paths) {
    const cut = entry.path.lastIndexOf('[]');
    const listPath = cut === -1 ? '' : entry.path.slice(0, cut + 2);
    if (!groups.has(listPath)) {
      let count = null;
      if (listPath) {
        // 0.49.22 - Every inner item for a list inside a list: the rows
        // "Rows from" that path gives.
        if (listDepth(listPath) > 1) count = apiRows(response, listPath).length;
        else {
          const rows = readPath(response, listPath.slice(0, -2));
          count = Array.isArray(rows) ? rows.length : null;
        }
      }
      groups.set(listPath, { listPath, count, paths: [] });
    }
    groups.get(listPath).paths.push({
      ...entry,
      // What a column would be called: the part after the list.
      relative: listPath ? entry.path.slice(listPath.length + 1) : entry.path,
    });
  }

  // Response-level first: it is the shorter list and the one an admin
  // reads before deciding whether a table is needed at all.
  return Array.from(groups.values())
    .sort((a, b) => (a.listPath === '' ? -1 : b.listPath === '' ? 1 : a.listPath.localeCompare(b.listPath)));
}


/* 0.41.55 — Parse a response body by its content type.
 *
 * Mirrors parse_response in app/services/api_connections.py. XML is
 * converted to the same shape JSON gives, so every consumer — the path
 * list, table columns, api(), set_from_api — keeps working without
 * knowing XML exists.
 */
export function parseResponseBody(text, contentType) {
  const media = String(contentType || '').split(';')[0].trim().toLowerCase();
  const looksXml = media.includes('xml');

  if (!looksXml) {
    try {
      return { value: expandEmbedded(JSON.parse(text || '{}')), error: null };
    } catch {
      // A service can return XML under the wrong content type, so fall
      // through rather than fail on the header alone.
    }
  }

  // 0.43.46 - Never parse a reply that declares a DOCTYPE.
  if (/<!doctype/i.test(text || '')) {
    return { value: null, error: 'response declares a DOCTYPE, which is refused' };
  }
  try {
    const doc = new DOMParser().parseFromString(text || '', 'application/xml');
    if (doc.querySelector('parsererror')) {
      return { value: null, error: 'response is neither JSON nor valid XML' };
    }
    const root = doc.documentElement;
    return { value: expandEmbedded({ [stripNs(root.nodeName)]: xmlToObj(root) }), error: null };
  } catch (err) {
    return { value: null, error: String(err) };
  }
}

/* `soap:Envelope` -> `Envelope`. Namespaces matter when parsing but
 * would make a path unusable in a rule, and a form never merges two
 * vocabularies. */
function stripNs(name) {
  const i = name.indexOf(':');
  return i === -1 ? name : name.slice(i + 1);
}

function xmlToObj(el) {
  const children = Array.from(el.children);
  const attrs = {};
  for (const a of Array.from(el.attributes || [])) {
    if (!a.name.startsWith('xmlns')) attrs['@' + stripNs(a.name)] = a.value;
  }

  if (children.length === 0) {
    const text = (el.textContent || '').trim();
    return Object.keys(attrs).length ? { ...attrs, '#text': text } : text;
  }

  const out = { ...attrs };
  for (const child of children) {
    const tag = stripNs(child.nodeName);
    const value = xmlToObj(child);
    if (tag in out) {
      // Repeated tag: a list, so `Rate[].ISO` works as `results[]` does.
      if (!Array.isArray(out[tag])) out[tag] = [out[tag]];
      out[tag].push(value);
    } else {
      out[tag] = value;
    }
  }
  return out;
}

/* 0.43.43 - The readable answer inside an AI reply.
 *
 * An AI service wraps its answer in its own envelope, and the answer is
 * often JSON written as TEXT inside that envelope, so the raw reply shows
 * it escaped: {\"verdict\": \"review\"}. This finds the answer text in
 * the shapes the common providers use and, when that text is itself a
 * JSON object, returns its fields too.
 *
 *   Claude   content[].text              (type 'text' blocks)
 *   Gemini   candidates[0].content.parts[].text  (thought parts skipped)
 *   OpenAI   choices[0].message.content
 *
 * Returns { text, fields } or null when the reply is none of these.
 * Display only: nothing here is stored or sent anywhere.
 */
export function extractAiAnswer(parsed) {
  if (!parsed || typeof parsed !== 'object') return null;
  let text = null;
  if (Array.isArray(parsed.content)) {
    const parts = parsed.content
      .filter(b => b && b.type === 'text' && typeof b.text === 'string')
      .map(b => b.text);
    if (parts.length) text = parts.join('\n');
  }
  if (text === null && Array.isArray(parsed.candidates) && parsed.candidates[0]) {
    const parts = ((parsed.candidates[0].content || {}).parts || [])
      .filter(p => p && typeof p.text === 'string' && !p.thought)
      .map(p => p.text);
    if (parts.length) text = parts.join('\n');
  }
  if (text === null && Array.isArray(parsed.choices) && parsed.choices[0]) {
    const c = (parsed.choices[0].message || {}).content;
    if (typeof c === 'string') text = c;
  }
  if (text === null) return null;
  // 0.43.45 - The JSON may sit anywhere in the answer: alone, in a
  // ```json fence, or after a sentence like "Here is the JSON
  // requested:". Tried in that order; the first JSON object wins.
  // Mirrors extract_ai_answer() in the backend.
  let t = text.trim();
  const candidates = [t];
  const fence = t.match(/```[a-zA-Z]*\s*([\s\S]*?)\s*```/);
  if (fence) candidates.push(fence[1]);
  const first = t.indexOf('{');
  const last = t.lastIndexOf('}');
  if (first >= 0 && first < last) candidates.push(t.slice(first, last + 1));
  let fields = null;
  for (const cand of candidates) {
    try {
      const inner = JSON.parse(cand);
      if (inner && typeof inner === 'object' && !Array.isArray(inner)) { fields = inner; break; }
    } catch { /* try the next */ }
  }
  // A fence-only answer reads better without its fence.
  if (fence && fence.index === 0 && fence[0].length === t.length) t = fence[1];
  return { text: t, fields };
}

/* 0.43.44 - An AI connector's parsed reply with `answer` added: the
 * answer text as answer.text and, when it is a JSON object, each field as
 * answer.<field>. Mirrors with_ai_answer() in the backend, which adds the
 * same key to the live reply rules read. */
// 0.43.47 - onAiCard: always add `answer`; otherwise only when the reply
// has an AI provider's shape (mirrors the backend).
export function withAiAnswer(parsed, onAiCard = true) {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return parsed;
  const found = extractAiAnswer(parsed);
  if (!found && !onAiCard) return parsed;
  const answer = { text: found ? found.text : '' };
  if (found && found.fields) {
    for (const [k, v] of Object.entries(found.fields)) {
      if (k !== 'text') answer[k] = v;
    }
  }
  return { ...parsed, answer };
}

/* 0.43.46 - Structured content INSIDE a text value.
 *
 * A value that is text but holds JSON or XML (an AI answer, escaped XML
 * in a SOAP element, a CDATA payload) is read and added beside it under
 * the same key plus »:  content[].text  and  content[].text».verdict.
 * Mirrors expand_embedded() in the backend, which does the same to the
 * live replies rules read, with the same guards: no DOCTYPE, a size cap,
 * at most three levels of text inside text. Text that only looks like
 * JSON or XML fails to parse and stays text.
 */
export const EMBED_MARK = '»';
const EMBED_MAX_CHARS = 200000;
const EMBED_MAX_DEPTH = 3;

function parseEmbedded(text) {
  const t = String(text || '').trim();
  if (!t || t.length > EMBED_MAX_CHARS) return undefined;
  if (t[0] === '{' || t[0] === '[') {
    try {
      const v = JSON.parse(t);
      return v && typeof v === 'object' ? v : undefined;
    } catch { return undefined; }
  }
  if (t[0] === '<') {
    if (/<!doctype/i.test(t)) return undefined;
    try {
      const doc = new DOMParser().parseFromString(t, 'application/xml');
      if (doc.querySelector('parsererror')) return undefined;
      const root = doc.documentElement;
      return { [stripNs(root.nodeName)]: xmlToObj(root) };
    } catch { return undefined; }
  }
  return undefined;
}

export function expandEmbedded(node, depth = 0) {
  if (Array.isArray(node)) return node.map(v => expandEmbedded(v, depth));
  if (node && typeof node === 'object') {
    const out = {};
    for (const [key, value] of Object.entries(node)) {
      out[key] = expandEmbedded(value, depth);
      if (typeof value === 'string' && depth < EMBED_MAX_DEPTH
          && !key.endsWith(EMBED_MARK)) {
        const inner = parseEmbedded(value);
        if (inner !== undefined) out[key + EMBED_MARK] = expandEmbedded(inner, depth + 1);
      }
    }
    return out;
  }
  return node;
}

/* 0.43.46 - Whether an AI reply says its answer was cut short.
 * Mirrors ai_answer_cut_off() in the backend. */
export function aiAnswerCutOff(parsed) {
  if (!parsed || typeof parsed !== 'object') return false;
  if (parsed.stop_reason === 'max_tokens') return true;
  const c = Array.isArray(parsed.candidates) && parsed.candidates[0];
  if (c && c.finishReason === 'MAX_TOKENS') return true;
  const ch = Array.isArray(parsed.choices) && parsed.choices[0];
  if (ch && ch.finish_reason === 'length') return true;
  return false;
}

/* 0.43.65 - Why an AI reply holds no usable answer, or null: [class,
 * reason]. 'incomplete_answer' when the token limit cut it,
 * 'ai_answer_failed' for any other abnormal ending (a malformed tool
 * call, a safety block, a refusal, a content filter). Mirrors
 * ai_answer_failure() in the backend. */
export function aiAnswerFailure(parsed) {
  if (!parsed || typeof parsed !== 'object') return null;
  const sr = parsed.stop_reason;
  if (sr === 'max_tokens') return ['incomplete_answer', 'max_tokens'];
  if (sr === 'refusal') return ['ai_answer_failed', 'refusal'];
  const c = Array.isArray(parsed.candidates) && parsed.candidates[0];
  if (c) {
    const fr = c.finishReason;
    if (fr === 'MAX_TOKENS') return ['incomplete_answer', fr];
    if (fr && fr !== 'STOP' && fr !== 'FINISH_REASON_UNSPECIFIED') {
      return ['ai_answer_failed', [fr, c.finishMessage].filter(Boolean).join(': ')];
    }
  }
  if (Array.isArray(parsed.candidates) && parsed.candidates.length === 0 && parsed.promptFeedback) {
    return ['ai_answer_failed', `prompt blocked: ${parsed.promptFeedback.blockReason || 'blocked'}`];
  }
  const ch = Array.isArray(parsed.choices) && parsed.choices[0];
  if (ch && ch.finish_reason === 'length') return ['incomplete_answer', 'length'];
  if (ch && ch.finish_reason === 'content_filter') return ['ai_answer_failed', 'content_filter'];
  return null;
}

/* 0.43.66 - Requests as rule and element targets.
 *
 * A connection's key is its reference name; one of its requests (an AI
 * prompt, a second endpoint) is `connection.request`. Reference names
 * cannot contain a dot, so the key is unambiguous, and `connection` alone
 * still means the `default` request, so every existing form keeps
 * working. Everything that picks a connection (rule effects, conditions,
 * api(), API-fed elements) stores and reads this key; only the call
 * splits it.
 *
 * expandApiTargets(connections) returns the connection list plus one
 * entry per non-default request, shaped like a connection (slug = key,
 * name "Connection > Request", that request's own inputs and paths), so
 * every picker offers requests without knowing about them. */
/* 0.43.67 - {'answer.verdict': ['ok', 'review'], ...} from a prompt's
 * declared answer fields: what the rule editor offers for "answer is". */
function answerValues(req) {
  const cfg = req && req.prompt_config;
  const out = {};
  if (!cfg || req.ignore_prompt_editor || cfg.answer_format !== 'json') return out;
  for (const f of cfg.answer_fields || []) {
    if (!f || !f.name) continue;
    out[`answer.${f.name}`] = f.type === 'choice' ? (f.values || [])
      : f.type === 'boolean' ? ['true', 'false'] : [];
  }
  return out;
}

/* 0.44.70 - A path marked as a date in Expected results also reaches
 * rules as <path>_iso (the ISO value), offered right after it. */
function withIsoPaths(paths, dates) {
  const marked = new Set(Object.entries(dates || {})
    .filter(([, d]) => d && !d.hint && (d.type === 'date' || d.type === 'datetime'))
    .map(([p]) => p));
  if (!marked.size) return paths || [];
  const out = [];
  for (const p of paths || []) {
    out.push(p);
    if (marked.has(p) && !(paths || []).includes(`${p}_iso`)) out.push(`${p}_iso`);
  }
  return out;
}

export function expandApiTargets(connections) {
  const out = [];
  for (const c of connections || []) {
    const dflt = (c.requests || []).find(r => r && r.slug === 'default');
    out.push({
      ...c,
      response_paths: withIsoPaths(c.response_paths, (dflt && dflt.response_dates) || c.response_dates),
      answer_values: answerValues(dflt),
    });
    for (const r of c.requests || []) {
      if (!r || r.slug === 'default') continue;
      out.push({
        ...c,
        slug: `${c.slug}.${r.slug}`,
        name: `${c.name} \u203a ${r.name}`,
        inputs: r.inputs || [],
        response_paths: withIsoPaths(r.response_paths || [], r.response_dates),
        request_slug: r.slug,
        answer_values: answerValues(r),
      });
    }
  }
  return out;
}

/* [connection, request] from a key; request is '' for `default`. */
export function splitApiTarget(key) {
  const k = String(key || '');
  const i = k.indexOf('.');
  return i < 0 ? [k, ''] : [k.slice(0, i), k.slice(i + 1)];
}


/* 0.49.22 - Rows from a list inside a list.
 *
 * `result.specializations[].areas[]` reads, for every specialization,
 * every area, and gives ONE ROW PER AREA. readPath would give a list of
 * lists, which a table or a repeating display cannot draw.
 *
 * Each row is the inner item's own fields (an object) or `value` (a
 * text or a number), plus:
 *   index         its place in its own list, from 1
 *   parent        the outer item, the same way (fields or value, index)
 *   parent.parent the one above that, for three lists, and so on
 * A field of the item's own with one of these names wins, so nothing
 * the API sends is hidden.
 *
 * keepEmpty: an outer item whose inner list is empty or missing still
 * gives one row, with an empty value, so a group with no items shows.
 *
 * A path with one list (or none) is read exactly as before, so every
 * table and display configured earlier is unchanged. */
export function listDepth(path) {
  const steps = parsePath(path);
  return steps ? steps.filter(s => s.kind === 'all').length : 0;
}

function rowOf(item, index, parent) {
  const base = item !== null && typeof item === 'object' && !Array.isArray(item)
    ? item : { value: item === null || item === undefined ? '' : item };
  const row = { index, ...base };
  if (parent !== undefined && !Object.prototype.hasOwnProperty.call(base, 'parent')) row.parent = parent;
  return row;
}

function asList(node) {
  if (Array.isArray(node)) return node;
  if (node === null || node === undefined) return [];
  return [node];
}

export function apiRows(response, path, { keepEmpty = false } = {}) {
  const steps = parsePath(path);
  if (steps === null) return [];
  const lists = steps.filter(s => s.kind === 'all').length;
  if (lists < 2) {
    const raw = walk(response, steps);
    return Array.isArray(raw) ? raw : [];
  }
  // The steps between one [] and the next.
  const segs = [[]];
  for (const s of steps) {
    if (s.kind === 'all') segs.push([]);
    else segs[segs.length - 1].push(s);
  }
  const tail = segs.pop();      // after the last [] (usually nothing)
  const out = [];
  const expand = (node, level, parent) => {
    const list = asList(walk(node, segs[level]));
    const last = level === segs.length - 1;
    if (list.length === 0 && keepEmpty && parent !== undefined) {
      out.push(rowOf('', 0, parent));
      return;
    }
    list.forEach((item, i) => {
      if (last) {
        const it = tail.length ? walk(item, tail) : item;
        out.push(rowOf(it, i + 1, parent));
      } else {
        expand(item, level + 1, rowOf(item, i + 1, parent));
      }
    });
  };
  expand(response, 0, undefined);
  return out;
}

/* The columns a row path offers, from a connection's recorded paths.
 * For one list: the paths under it (a further list last), as before.
 * For a list inside a list: the inner item's fields or `value`,
 * `index`, then the outer item's as `parent.…`. */
export function rowColumnPaths(paths, rowsPath, { withLists = true } = {}) {
  const prefix = String(rowsPath || '').trim();
  if (!prefix) return [];
  const all = Array.isArray(paths) ? paths : [];
  const under = (p) => {
    const out = [];
    for (const path of all) {
      if (!path.startsWith(p + '.')) continue;
      const rel = path.slice(p.length + 1);
      if (!out.includes(rel)) out.push(rel);
    }
    return out;
  };
  const order = (list) => [...list.filter(r => !r.includes('[]')),
                           ...(withLists ? list.filter(r => r.includes('[]')) : [])];
  if (listDepth(prefix) < 2) return order(under(prefix));
  // The list at each level, the innermost first.
  const levels = [];
  for (let at = prefix.indexOf('[]'); at !== -1; at = prefix.indexOf('[]', at + 2)) {
    levels.unshift(prefix.slice(0, at + 2));
  }
  const out = [];
  levels.forEach((list, depth) => {
    const lead = 'parent.'.repeat(depth);
    const child = depth > 0 ? levels[depth - 1].slice(list.length + 1) : '';
    const childKey = child.split('[')[0].split('.')[0];
    const own = under(list).filter(r => !child || (r.split('[')[0].split('.')[0] !== childKey));
    if (own.length === 0 || (depth === 0 && all.includes(list))) out.push(`${lead}value`);
    out.push(`${lead}index`);
    for (const r of order(own)) if (!out.includes(lead + r)) out.push(lead + r);
  });
  return out;
}
